'use client';

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Hls from 'hls.js';
import Particles from '@/components/ui/particles';
import useViewportActivation from '@/app/lib/hooks/use-viewport-activation';

export default function PettingCat() {
    const { t } = useTranslation('petting-cat');
    const videoRef = useRef<HTMLVideoElement>(null);
    const { ref: activationRef, isActive: shouldLoadVideo } =
        useViewportActivation<HTMLElement>({
            rootMargin: '400px 0px',
        });

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        let hls: Hls | null = null;

        if (!shouldLoadVideo) {
            video.pause();
            video.removeAttribute('src');
            video.load();
            return;
        }

        const src = `${process.env.NEXT_PUBLIC_VIDEO_BASE_URL}/home/petting-cat-hls/index.m3u8`;

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari 原生支持 HLS
            video.src = src;
            video.addEventListener('loadedmetadata', () => {
                video.play().catch(console.error);
            });
        } else if (Hls.isSupported()) {
            hls = new Hls();
            hls.loadSource(src);
            hls.attachMedia(video);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                video.play().catch(console.error);
            });
        }

        return () => {
            if (hls) {
                hls.destroy();
            }
            video.pause();
            video.removeAttribute('src');
            video.load();
        };
    }, [shouldLoadVideo]);

    return (
        <section
            ref={activationRef}
            className="relative z-10 flex w-full flex-col items-center bg-black text-white"
        >
            {/* Top Text */}
            <div className="tablet:max-w-[800px] tablet:text-center tablet:pb-[48px] desktop:pb-[60px] j-h4 tablet:j-h3 w-[87.5vw] max-w-[87.5vw] pb-[32px] text-start">
                <p>{t('topTitle.part1')}</p>
                <p>{t('topTitle.part2')}</p>
                <p>
                    <span className="bg-[linear-gradient(90deg,#7A58FF_0%,#E6A8FD_100%)] bg-clip-text text-transparent drop-shadow-sm [-webkit-background-clip:text] [-webkit-text-fill-color:transparent]">
                        {t('topTitle.part3')}
                    </span>
                </p>
            </div>

            {/* Video Container */}
            <div className="tablet:max-w-[800px] relative aspect-[800/648] w-[87.5vw] max-w-[87.5vw] overflow-hidden rounded-[8px]">
                <video
                    ref={videoRef}
                    playsInline
                    autoPlay
                    muted
                    loop
                    className="absolute inset-0 h-full w-full object-cover"
                    onContextMenu={(e) => e.preventDefault()}
                />
            </div>

            {/* Bottom Card */}
            <div className="m-mobile:w-[87.5vw] tablet:max-w-[87.5vw] desktop:max-w-[1440px] m-mobile:rounded-[16px] tablet:border-x tablet:h-[600px] tablet:mt-[120px] desktop:mt-[160px] relative mt-[80px] flex h-[400px] w-full max-w-[480px] flex-col items-center justify-center overflow-hidden rounded-none border-x-0 border-y border-[#6A696C] bg-[#0a0a0a]">
                <div className="absolute inset-0">
                    {shouldLoadVideo && (
                        <Particles
                            particleCount={270}
                            particleSpread={6}
                            speed={0.12}
                            particleColors={['#ffffff', '#ffffff', '#ffffff']}
                            moveParticlesOnHover={false}
                            particleHoverFactor={2}
                            alphaParticles={false}
                            particleBaseSize={70}
                            sizeRandomness={0.9}
                            cameraDistance={27}
                            disableRotation={false}
                        />
                    )}
                </div>

                {/* Text */}
                <div className="tablet:text-center j-h4 tablet:j-h2 relative z-10 w-[87.5%] text-start">
                    <p className="text-white">{t('bottomTitle.part1')}</p>
                    <p>
                        <span className="bg-[linear-gradient(90deg,#7A58FF_0%,#E6A8FD_100%)] bg-clip-text text-transparent [-webkit-background-clip:text] [-webkit-text-fill-color:transparent]">
                            {t('bottomTitle.part2')}
                        </span>
                    </p>
                </div>

                {/* Bottom Light Effect */}
                <div className="pointer-events-none absolute bottom-0 left-0 h-[200px] w-full overflow-hidden">
                    {/* Layer 1: #765EFF → #FFCFAA */}
                    <div
                        className="absolute bottom-[-80px] left-[5%] h-[60px] w-[90%] opacity-80 blur-[60px]"
                        style={{
                            background:
                                'linear-gradient(90deg, #765EFF 0%, #FFCFAA 100%)',
                        }}
                    />
                    {/* Layer 2: multicolor rainbow */}
                    <div
                        className="absolute bottom-[-40px] left-[5%] h-[30px] w-[90%] opacity-70 blur-[30px]"
                        style={{
                            background:
                                'linear-gradient(90deg, #C1B1ED 0%, #7F62D3 23.5%, #537CF7 53.15%, #E356FD 77.87%, #FBDE7A 100%)',
                        }}
                    />
                </div>
            </div>
        </section>
    );
}
